import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, Clock3, Database, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const REFRESH_MS = 5 * 60 * 1000;

const finite = (value) => Number.isFinite(Number(value));
const number = (value, fallback = 0) => finite(value) ? Number(value) : fallback;
const bps = (value) => finite(value) ? `${Number(value).toFixed(2)} bps` : "--";
const funding = (value) => finite(value) ? `${(Number(value) * 100).toFixed(4)}%` : "--";
const compactUsd = (value) => {
  if (!finite(value)) return "--";
  const amount = Number(value);
  if (Math.abs(amount) >= 1e9) return `$${(amount / 1e9).toFixed(2)}B`;
  if (Math.abs(amount) >= 1e6) return `$${(amount / 1e6).toFixed(1)}M`;
  if (Math.abs(amount) >= 1e3) return `$${(amount / 1e3).toFixed(1)}K`;
  return `$${amount.toFixed(2)}`;
};

const displayTime = (value) => {
  if (!value) return "No timestamp";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Timestamp unavailable"
    : date.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

function WeexRow({ row, depth }) {
  const gap = number(row.funding_gap);
  const basis = number(row.basis_bps);
  const spread = number(depth?.spread_bps);
  const pressure = gap > 0 ? "WEEX longer" : gap < 0 ? "WEEX shorter" : "Balanced";

  return (
    <article className="weex-market-row">
      <div className="weex-market-symbol">
        <strong>{row.symbol || "--"}</strong>
        <span>{pressure}</span>
      </div>
      <div><span>WEEX MARK</span><strong>{compactUsd(row.weex_px)}</strong></div>
      <div><span>WEEX FUNDING</span><strong className={number(row.weex_funding) >= 0 ? "is-positive" : "is-negative"}>{funding(row.weex_funding)}</strong></div>
      <div><span>VS OKX GAP</span><strong className={gap >= 0 ? "is-warning" : "is-neutral"}>{funding(row.funding_gap)}</strong></div>
      <div><span>BASIS</span><strong className={basis >= 0 ? "is-positive" : "is-negative"}>{bps(row.basis_bps)}</strong></div>
      <div><span>SPREAD</span><strong className={spread > 8 ? "is-warning" : "is-neutral"}>{bps(depth?.spread_bps)}</strong></div>
      <div><span>$25K BUY SLIP</span><strong>{bps(depth?.buy_slip_bps_25000)}</strong></div>
    </article>
  );
}

export default function WeexMarketPanel() {
  const [sources, setSources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    setLoading(true);
    try {
      const response = await fetch(`${API}/researcher/lab/weex`, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const body = await response.json();
      setSources(body?.sources || null);
      setError("");
    } catch {
      setError("WEEX market feed is temporarily unavailable.");
    } finally {
      window.clearTimeout(timeout);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const listings = sources?.weex_listings?.summary;
  const fetchedAt = sources?.weex_funding_basis?.fetched_at || sources?.weex_depth?.fetched_at;
  const merged = useMemo(() => {
    const rows = sources?.weex_funding_basis?.rows || [];
    const depthRows = sources?.weex_depth?.rows || [];
    return rows.map((row) => ({
      row,
      depth: depthRows.find((item) => String(item.symbol).toUpperCase() === String(row.symbol).toUpperCase()),
    }));
  }, [sources]);

  return (
    <section className="market-room-panel weex-market-panel">
      <div className="market-room-panel-head weex-market-head">
        <div>
          <span>WEEX EXECUTION INTELLIGENCE</span>
          <strong>Funding gap · basis · depth · estimated slippage</strong>
        </div>
        <div className="weex-market-status">
          <span className={error ? "is-offline" : "is-live"}><i />{error ? "OFFLINE" : "LIVE API"}</span>
          <small><Clock3 />{displayTime(fetchedAt)}</small>
          <button type="button" onClick={load} disabled={loading} title="Refresh WEEX feed">
            <RefreshCw className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {error && !sources ? (
        <div className="weex-market-empty"><Database /><strong>WEEX feed unavailable</strong><span>{error}</span></div>
      ) : loading && !sources ? (
        <div className="weex-market-empty"><Activity className="animate-pulse" /><strong>Loading WEEX</strong><span>Waiting for the live market endpoint.</span></div>
      ) : merged.length ? (
        <div className="weex-market-table">
          {merged.map(({ row, depth }) => <WeexRow key={row.symbol} row={row} depth={depth} />)}
        </div>
      ) : (
        <div className="weex-market-empty"><Database /><strong>No WEEX rows</strong><span>The endpoint returned no current pairs.</span></div>
      )}

      {listings ? (
        <footer className="weex-market-footer">
          <span>{listings.total ?? "--"} markets</span>
          <span>{listings.spot ?? "--"} spot</span>
          <span>{listings.swap ?? "--"} perpetual</span>
          <span>{listings.new_count ? `${listings.new_count} new listings` : "No new listings"}</span>
        </footer>
      ) : null}
    </section>
  );
}
